import { createRouter, createWebHistory } from 'vue-router'
import PaymentCancel from '../components/payment/PaymentCancel.vue'
import PaymentSuccess from '../components/payment/PaymentSuccess.vue'
import MainContent from '../components/layout/MainContent.vue'
import MaterialLibrary from '../components/materials/MaterialLibrary.vue'
import MaterialCategory from '../components/materials/MaterialCategory.vue'
import MaterialAdmin from '../components/materials/MaterialAdmin.vue'
import AudioPlayer from '../components/player/AudioPlayer.vue'
import TermsOfService from '../components/legal/TermsOfService.vue'
import PrivacyPolicy from '../components/legal/PrivacyPolicy.vue'
import RefundPolicy from '../components/legal/RefundPolicy.vue'
import LearnArticle from '../components/learn/LearnArticle.vue'
import LearnIndex from '../components/learn/LearnIndex.vue'
import PartnerPage from '../components/partner/PartnerPage.vue'
import PartnerAdmin from '../components/partner/PartnerAdmin.vue'
import { isReferralCodeFormat, setPendingReferralCode } from '@/composables/useReferral'

function normalizeLegacyPaymentHash() {
  if (typeof window === 'undefined') return

  const match = window.location.hash.match(/^#(\/payment\/(?:success|cancel)(?:\?.*)?)$/)
  if (!match) return

  window.history.replaceState(window.history.state, '', match[1])
}

normalizeLegacyPaymentHash()

// 首页和素材库各有中英两套地址：英文在 /…，中文在 /zh/…。
// meta.page 是不分语言的页面名，meta.locale 是这个地址对应的界面语言。
export function localizedRouteName(page, locale) {
  return locale === 'zh' ? `Zh${page}` : page
}

function localizedRoutes(page, path, record) {
  return ['en', 'zh'].map(locale => ({
    ...record,
    path: locale === 'zh' ? `/zh${path}` : path || '/',
    name: localizedRouteName(page, locale),
    meta: { ...record.meta, page, locale }
  }))
}

const routes = [
  { path: '/check-in', name: 'CheckIn', component: MainContent },
  ...['transcribe', 'youtube', 'speech'].map(tool => ({
    path: `/create/${tool}`,
    name: `Create-${tool}`,
    component: MainContent,
    meta: { creationTool: tool }
  })),
  ...localizedRoutes('Home', '', {
    component: MainContent,
    meta: {
      keepAlive: true
    }
  }),
  // 登录注册页盖在工作区之上，底下仍然渲染 MainContent
  {
    path: '/login',
    name: 'Login',
    component: MainContent
  },
  {
    path: '/signup',
    name: 'Signup',
    component: MainContent
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: MainContent
  },
  // 购买页同样盖在工作区之上
  {
    path: '/pricing',
    name: 'Pricing',
    component: MainContent
  },
  ...localizedRoutes('Material', '/material', {
    component: MaterialLibrary,
    meta: {
      keepAlive: true
    }
  }),
  ...localizedRoutes('Profile', '/profile', {
    component: MainContent
  }),
  ...localizedRoutes('LevelTest', '/level-test', {
    component: MainContent,
    beforeEnter: to => (['listening', 'speaking'].includes(to.query.skill)
      ? true
      : { name: localizedRouteName('Profile', to.meta.locale), replace: true })
  }),
  ...localizedRoutes('MaterialCategory', '/material/:id', {
    component: MaterialCategory,
    props: true,
    // 旧链接 /material/:id?collection=xx 跳到合集地址
    beforeEnter: to => (to.query.collection
      ? {
          name: localizedRouteName('MaterialCollection', to.meta.locale),
          params: { id: to.params.id, collectionId: to.query.collection }
        }
      : true),
    meta: {
      keepAlive: false // 设置为 false，确保每次都重新加载
    }
  }),
  ...localizedRoutes('MaterialCollection', '/material/:id/:collectionId', {
    component: MaterialCategory,
    props: true,
    meta: {
      keepAlive: false
    }
  }),
  // 合集里的单套题，如 /material/ielts/12/test-6，只列出这一套的几个 Section
  ...localizedRoutes('MaterialTest', '/material/:id/:collectionId/:test(test-\\d+)', {
    component: MaterialCategory,
    props: true,
    meta: {
      keepAlive: false
    }
  }),
  {
    path: '/admin/materials',
    alias: '/material-admin',
    name: 'MaterialAdmin',
    component: MaterialAdmin
  },
  {
    path: '/admin/partners',
    name: 'PartnerAdmin',
    component: PartnerAdmin
  },
  // 推广伙伴：介绍、申请和推广中心都在这一页
  {
    path: '/partner',
    name: 'Partner',
    component: PartnerPage
  },
  {
    path: '/payment/cancel',
    name: 'PaymentCancel',
    component: PaymentCancel
  },
  {
    path: '/payment/success',
    name: 'PaymentSuccess',
    component: PaymentSuccess
  },
  {
    path: '/terms',
    name: 'TermsOfService',
    component: TermsOfService
  },
  {
    path: '/privacy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicy
  },
  {
    path: '/refund',
    name: 'RefundPolicy',
    component: RefundPolicy
  },
  {
    path: '/learn',
    name: 'LearnIndex',
    component: LearnIndex
  },
  {
    path: '/zh/learn',
    name: 'ZhLearnIndex',
    component: LearnIndex
  },
  {
    path: '/learn/:slug',
    name: 'LearnArticle',
    component: LearnArticle,
    props: true
  },
  {
    path: '/zh/learn/:slug',
    name: 'ZhLearnArticle',
    component: LearnArticle,
    props: true
  }
]

export const authRouteNames = ['Login', 'Signup', 'ResetPassword']

// 英文首页是 /，中文首页是 /zh，两者渲染同一个工作区
export function isHomePath(path) {
  return path === '/' || path === '/zh'
}

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// 邀请链接 ?ref=CODE：记下邀请码，直接进注册页；格式不对的只把参数去掉
router.beforeEach((to) => {
  if (!to.query.ref) return true
  const { ref, ...query } = to.query
  if (isReferralCodeFormat(ref)) {
    setPendingReferralCode(ref)
    return { name: 'Signup', replace: true }
  }
  return { path: to.path, query, hash: to.hash, replace: true }
})

export default router
